import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { isSubmitting, submit } from 'redux-form'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
// constants
import { Forms, Modals } from 'constants/index'
import { REVIEW_STATUSES } from 'constants/review'
// view components
import CreateReviewForm from 'components/Modals/CreateReviewModal/components/ReviewDetails/components/Form'
import ActivitiesListContainer from 'containers/Activities/components/ActivitiesList'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// hooks
import { useCurrentUser, useModalData } from 'hooks/index'
import { useApproveReview, useReviewMutations, useReviewQuestions } from 'hooks/reviews'
// graphql
import { REVIEW_ACTIVITIES_LIST_QUERY } from 'graphql/reviews/queries'
// views
import { ButtonsWrapper, Paper, Scrollbar, Wrapper } from './views'
// utils
import { get } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { isReviewApprover, mapReviewPicklistsToFormValues } from 'utils/reviews'
// types
import { LogEntityTypes } from 'types/graphql'

const ReviewDetails: React.FC = () => {
  const dispatch = useDispatch()
  const currentUser = useCurrentUser()

  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  const {
    hideModal,
    updateModal,
    modalData: { isUpdate, isApprove, review, entity }
  } = useModalData({ modalType: Modals.REVIEW_CREATE_MODAL })

  const { questions, picklists } = useReviewQuestions(entity?.entityType)
  const { createReview, updateReview } = useReviewMutations()
  const { approveReview, isApproving } = useApproveReview()

  // the status is picked by the clicked button, while the form data arrives later via redux-form onSubmit
  const reviewStatus = React.useRef(REVIEW_STATUSES.NOT_STARTED)

  const submitForm = (status: string) => {
    reviewStatus.current = status
    dispatch(submit(Forms.CREATE_REVIEW_FORM))
  }

  const handleSaveDraft = () => submitForm(REVIEW_STATUSES.IN_PROGRESS)

  const handleCreateReview = () => submitForm(REVIEW_STATUSES.COMPLETE)

  const handleFormSubmit = async (data) => {
    try {
      await treeQueryRefetch?.()

      if (review.status === REVIEW_STATUSES.IN_PROGRESS || isUpdate) {
        await updateReview({ data, status: reviewStatus.current })
      } else {
        await createReview({ data, status: reviewStatus.current })
      }

      hideModal()
    } catch (error) {
      handleError(error, 'ReviewDetails.handleFormSubmit')
    }
  }

  // attachments need a saved review, so the first upload creates it as a draft
  const beforeUpload = async () => {
    const createdReview = await createReview({ status: REVIEW_STATUSES.IN_PROGRESS })

    updateModal({ review: { ...review, id: createdReview.id, status: REVIEW_STATUSES.IN_PROGRESS } })

    return { ...createdReview, entityType: LogEntityTypes.Review }
  }

  const handleApproveReview = async () => {
    const isApproved = await approveReview(review, entity)

    if (isApproved) {
      hideModal()
    }
  }

  const initialValues = {
    goals: get(review, 'values.goals'),
    doWell: get(review, 'values.doWell'),
    doBetter: get(review, 'values.doBetter'),
    picklists: mapReviewPicklistsToFormValues(picklists, review)
  }

  const isReviewInProgress = get(review, 'status') === REVIEW_STATUSES.IN_PROGRESS
  const isReviewComplete = get(review, 'status') === REVIEW_STATUSES.COMPLETE

  const submitting = useSelector((state) => isSubmitting(Forms.CREATE_REVIEW_FORM)(state))

  return (
    <Paper>
      <Scrollbar>
        <Wrapper container direction={'column'} wrap={'nowrap'}>
          <CreateReviewForm
            isReadOnly={isApprove}
            questions={questions}
            picklists={picklists}
            onSubmit={handleFormSubmit}
            initialValues={initialValues}
          />
          <ActivitiesListContainer
            beforeUpload={!get(review, 'id') && beforeUpload}
            isReviewDetails
            isReadOnly={isApprove}
            disableDrag
            skip={!get(review, 'id')}
            entity={{ id: review.id, entityType: LogEntityTypes.Review }}
            query={REVIEW_ACTIVITIES_LIST_QUERY}
            variables={{
              filter: { entityId: review.id }
            }}
          />
        </Wrapper>
      </Scrollbar>

      <ButtonsWrapper isReviewInProgress={isReviewInProgress}>
        {!isApprove && (
          <Button type={'button'} isLoading={submitting} layout={BUTTON_LAYOUTS.PURPLE} onClick={handleCreateReview}>
            {isUpdate && !isReviewInProgress ? 'Submit changes' : 'Submit review'}
          </Button>
        )}
        {!isApprove && !isReviewComplete && (
          <Button type={'button'} layout={BUTTON_LAYOUTS.WHITE} onClick={handleSaveDraft}>
            {isReviewInProgress ? 'Submit changes' : 'Save draft'}
          </Button>
        )}
        {isReviewApprover(review, currentUser) && (
          <Button type={'button'} isLoading={isApproving} layout={BUTTON_LAYOUTS.PURPLE} onClick={handleApproveReview}>
            Approve Review
          </Button>
        )}
      </ButtonsWrapper>
    </Paper>
  )
}

export default ReviewDetails
